import {
    ErrorResponse,
    InternalErrorResponse,
    TSourceKey,
    TUTMMediumKey,
} from 'shared/types';
import { ErrorMessages } from '../../../constants/errorMessages';

/* Errors */
type ErrorTypes =
    | 'customer_not_found'
    | 'payload_parse_exception'
    | 'customer_to_young'
    | 'dob_update_to_frequent'
    | 'phone_not_available';
type DetailsType = 'field_error' | 'rule_error';

type DetailsCode =
    | 'required'
    | 'invalid'
    | 'blank'
    | 'max_length'
    | 'min_length'
    | 'datetime;';

type DetailsMessage = Record<string, string[]> | string;

export type CustomerErrorResponse = ErrorResponse<
    ErrorTypes,
    DetailsType,
    DetailsCode,
    DetailsMessage
>;

/* Common */

export type TCustomerData = {
    phone_number: string;
    birthday: string;
    full_name: string;
    customer_id: string;
    email?: string;
};

/* Update customer */

export type UpdateCustomerErrorResponse =
    | CustomerErrorResponse
    | InternalErrorResponse;

export type UpdateCustomerPayload = Partial<Omit<TCustomerData, 'customer_id'>>;

// response is 200 ok with empty response body
export type UpdateCustomer = (
    customerId: string,
    payload: UpdateCustomerPayload
) => Promise<Response>;

export type TUpdateCustomerUtilReturn = {
    error: Partial<
        Record<keyof Omit<TCustomerData, 'customer_id'> | 'generic', string>
    > | null;
};

/* Join customer */

export type JoinCustomerPayload = {
    merchant_id: string;
    source: TSourceKey;
    medium?: TUTMMediumKey;
    campaign_id?: string;
};

export type TJoinCustomerUtilReturn = {
    error: string | null;
};

export type JoinCustomerErrorResponse = ErrorResponse | InternalErrorResponse;

// response is 200 ok with empty response body
export type JoinCustomer = (
    customerId: string,
    payload: JoinCustomerPayload
) => Promise<Response>;

/* Create customer */

export type CreateCustomerErrorResponse =
    | ErrorResponse<
          ErrorTypes,
          DetailsType,
          DetailsCode,
          {
              customer_info: Record<
                  keyof Partial<Omit<TCustomerData, 'customer_id'>>,
                  string[]
              >;
          }
      >
    | InternalErrorResponse;

export type CreateCustomerResponse = Pick<TCustomerData, 'customer_id'>;

export type CreateCustomerPayload = {
    merchant_id: string;
    email?: string;
    source: TSourceKey;
    medium?: TUTMMediumKey;
    customer_info?: Partial<Omit<TCustomerData, 'customer_id'>>;
    campaign_id?: string;
};

export type VerifyPhonePayload = {
    phone_number: string;
    code: string;
    customer_id: string;
};

// response is 200 ok with empty response body
export type CreateCustomer = (
    payload: CreateCustomerPayload
) => Promise<Response>;

export type TCreateCustomerUtilReturn = {
    error: Record<
        keyof Omit<TCustomerData, 'customer_id'> | 'email' | 'generic',
        string
    > | null;
    data: CreateCustomerResponse | null;
};

/* Get customer status */

export enum CustomerStatus {
    // Customer doesn't exist
    NOT_FOUND = 'not_found',
    // Customer exists, but is not connected to merchant
    NOT_CONNECTED = 'not_connected',
    // Customer exists and is connected to merchant
    CONNECTED = 'connected',
    // Customer data is conflicting with what is currently in database
    CONFLICT = 'conflict',
}

export type TCustomerStatus = `${CustomerStatus}`;

export type GetCustomerStatusResponse = {
    status: TCustomerStatus;
    customer_id: string;
};

export type GetCustomerStatusErrorResponse = ErrorResponse;

export type GetCustomerStatus = (args: {
    merchantId: string;
    email?: string;
    phone_number?: string;
}) => Promise<Response>;

export type TGetCustomerStatusUtilReturn = {
    error: Record<'email' | 'phone_number' | 'generic', string> | null;
    data: GetCustomerStatusResponse | null;
};

export type TPostPhoneVerificationUtilReturn = {
    error: TPostSendPhoneVerificationError | ErrorMessages | null;
};

export type TPostVerifyPhoneUtilReturn = {
    error: TPostVerifyPhoneError | ErrorMessages | null;
};

export type TGetMerchantsPhoneNumber = Promise<{
    error: ErrorMessages | null;
    data: { phone_number: string } | null;
}>;

export type TPostVerifyPhoneError =
    | 'phone_already_verified'
    | 'phone_code_invalid';

export type TPostSendPhoneVerificationError =
    | 'phone_verified_too_many_times'
    | 'phone_number_has_unsubscribed';
